// Which examining board a marking belongs to.
//
// The Essay Lab is ONE lab with a switch. NSC (CAPS) and IEB sell as separate
// products — Essay Lab NSC, Essay Lab IEB, each its own course — but under the
// hood it is one slide type, one endpoint, one allowance, one pipeline. What
// the board picks is CONTENT: the question set, the memos, and the marking grid
// the engine reads. A fix therefore lands once.
//
// WHY THIS IS A SECOND COPY OF THE ENGINE'S `Board`, AND HAS TO BE
//
// marking-coach-engine exports the same type — but its barrel carries
// `memoContent`, the official memos' expected answers for every question, so a
// VALUE import from client code ships the mark scheme to anyone who fetches the
// chunk. `services/markingAllowanceService.ts` carries the same warning for the
// same reason. The slide editor needs the board list to draw a picker, which is
// value, not type. So the two-item list lives here.
//
// The two are made to agree in one place: `api/ai/mark-essay.ts` refuses, with a
// 400, any board this list carries that the engine's does not. That check is not
// decoration. Without it the engine's own `asBoard` resolves the unknown board
// to NSC, an NSC context is built for it, and the marking is only stopped
// several steps later by the board CHECK on `marking_usage` — a 500 that reads
// as a database fault rather than as a board nobody has built content for.

export const BOARDS = ['nsc', 'ieb'] as const;

export type Board = (typeof BOARDS)[number];

/**
 * What a slide, a request or a stored marking means when it says nothing.
 *
 * NSC, because everything that existed before boards did was NSC: every
 * authored slide, every question pack, every row in `marking_usage`. A missing
 * board is an old caller, not an unknown one.
 */
export const DEFAULT_BOARD: Board = 'nsc';

export const BOARD_LABELS: Record<Board, string> = {
  nsc: 'NSC (CAPS)',
  ieb: 'IEB',
};

export function isBoard(value: unknown): value is Board {
  return typeof value === 'string' && (BOARDS as readonly string[]).includes(value);
}

/** Coerce anything — slide data, a request body, a query param — to a board. */
export function asBoard(value: unknown): Board {
  return isBoard(value) ? value : DEFAULT_BOARD;
}

export function boardLabel(value: unknown): string {
  return BOARD_LABELS[asBoard(value)];
}

/**
 * The AI-credit ledger's `feature` tag for a board's marking passes.
 *
 * Separate tags so the two products' model spend is separable in the ledger and
 * in the Hub's credit reporting — they are sold apart, and "what does Essay Lab
 * IEB cost us to run" should not need a join through courses to answer.
 *
 * NSC KEEPS `essay_marking` UNCHANGED. The ledger is append-only and every
 * marking ever taken is tagged with it; renaming would split one product's
 * history across two tags at an arbitrary date and make every before/after
 * comparison wrong. The new board gets the new tag; the old one keeps its name.
 *
 * This is deliberately NOT a second tenant feature flag. `essay_marking` gates
 * whether the tile appears in a tenant's builder at all, and both boards are
 * sold from the same tenant as different COURSES — the thing a learner buys is
 * the course, and the allowance is already scoped to it. A per-board
 * entitlement flag would gate the author's picker, which is not where the money
 * is.
 */
export function markingLedgerFeature(board: Board): string {
  return board === 'nsc' ? 'essay_marking' : `essay_marking_${board}`;
}
